import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, NoResults } from 'basic-components';
import { Query } from 'dashboard-services';
import { Fetching, globalActions, GroupFilter } from 'primary-components';

import { basicDatalakeActions } from 'actions/datalake';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/datalake';

import AddFileRow from './AddFileRow';

import { When } from 'react-if';
import InfiniteScroll from 'react-infinite-scroller';

import classNames from 'classnames';
import './AddNewFile.scss';

const AddNewFile = () => {
  const dispatch = useDispatch(),
        [groupName, setGroupName] = useState(),
        { address, query } = useSelector(state => state.datalakeState),
        searchRef = useRef(),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        finalQuery = useMemo(() => {
          let finalQuery = `*${query || ""}*.csv&latest=true`
          if(groupName) {
            finalQuery += `&groupName.keyword=${groupName}`
          }
          return finalQuery;
        }, [groupName, query]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
          .withFetching(false)
        , [getApiConfigLocal]),
        select = useCallback(data => data?.pages?.flatMap(page => page?.items), []),
        { data: files, isPending, isFetchingNextPage, hasNextPage, fetchNextPage: onScroll } = filesDatalakeQueries.useSearchFiles({ 
          query: finalQuery, 
          shouldBeLimited,
          getQueryBuilder,
          select
        }),
        scrollRef = useRef(),
        cancelEditing = useCallback(() => dispatch(basicDatalakeActions.clear()), [dispatch])

  useEffect(() => () => {
    dispatch(basicDatalakeActions.clear())
  }, [dispatch])

  useEffect(() => () => {
    searchRef.current?.cancel?.()
  }, [])

  return (
    <div className="ng-office-app__authed__content__body__item ng-office-app__authed__content__body__item--add-file">
      <div className="ng-office-app__authed__content__body__item__headers">
        <div className="ng-office-app__authed__content__body__item__headers__header">
          Name
        </div>
        <div className="ng-office-app__authed__content__body__item__headers__header ng-office-app__authed__content__body__item__headers__header--group">
          <GroupFilter
              className="ng-office-app__authed__content__body__item__headers__header__group-filter"
              groupName={groupName}
              onChange={setGroupName}
              size="normal"
          />
        </div>
      </div>
      <Fetching isFetching={isFetchingNextPage || isPending}/>
      <div 
          className={classNames(
            "ng-office-app__authed__content__body__item__body", 
            { "ng-office-app__authed__content__body__item__body--with-footer": address }
          )}
          ref={scrollRef}
      >
        <When condition={files?.length === 0 && !isPending}>
          {() => (
            <div className="no-results-wrapper">
              <NoResults
                  optionalText={query ? "Try to search a diffrent keyword" : "Navigate to Add New tab to add data."}
                  title="No data has been found."
              />
            </div>
          )}
        </When>
        <When condition={files?.length > 0 && !isPending}>
          {() => (
            <InfiniteScroll
                className="ng-office-app__authed__content__body__item__body__scroll"
                hasMore={hasNextPage}
                initialLoad={false}
                loadMore={onScroll}
                threshold={800}
                useWindow={false}
            >
            {files.map(({ groupName, fid, fileName, ...rest }, index) => (
              <AddFileRow
                  {...rest}
                  groupName={groupName}
                  id={fid}
                  key={fid + index}
                  name={fileName}
                  scrollRef={scrollRef}
              />
            ))}
            </InfiniteScroll>
          )}
        </When>
      </div>
      <When condition={address}>
        {() => (
          <div className="ng-office-app__authed__content__body__item__footer">
            <Button
                onClick={cancelEditing}
                size="small"
                variant="text"
            >
              <strong>
                Cancel editing formula
              </strong>
            </Button>
          </div>
        )}
      </When>
    </div>
  )
}

AddNewFile.defaultProps = {
  query: undefined,
}

AddNewFile.propTypes = {
  query: PropTypes.string,
}

export default AddNewFile;